import { dashboardApi } from '@/store/api/base/api.base';
import { walkPages } from '@/store/api/base/api.walk';
import { dropRoute, writeRoute } from '@/store/api/begateway/begateway.routes.cache';
import type { TypeAppDispatch } from '@/store/store';
import type { TypePageInfo } from '@/types/api/types.api';
import type { TypeLimitsTest, TypeRouteLimit, TypeRuleInput } from '@/types/begateway/types.begateway.limit';
import type { TypeRoute, TypeRouteFreezeInput, TypeRouteInput } from '@/types/begateway/types.begateway.route';
import type { TypeGatewayRegistry } from '@/types/begateway/types.begateway.service';

type TypeLimitsTestInput = { method: string; path: string; limit?: TypeRouteLimit; endpointLimit?: TypeRuleInput };

const PAGE_SIZE = 200;

const MAX_PAGES = 25;

type TypeRoutePage = { items: TypeRoute[]; page: TypePageInfo | null; registry: TypeGatewayRegistry | null };

type TypeGatewayRoutesList = { items: TypeRoute[]; registry: TypeGatewayRegistry | null };

export type TypeRouteRef = { kind: string; name: string };

export const routeKey = (route: TypeRouteRef): string => `${route.kind}/${route.name}`;

export const byKey = (a: TypeRoute, b: TypeRoute) => a.kind.localeCompare(b.kind) || a.name.localeCompare(b.name, 'it');

const target = (ref: TypeRouteRef) => `/begateway/routes/${encodeURIComponent(ref.kind)}/${encodeURIComponent(ref.name)}`;

const replaceRoute = (dispatch: TypeAppDispatch, queryFulfilled: Promise<{ data: TypeRoute }>) => queryFulfilled
    .then(({ data }) => writeRoute(dispatch, data))
    .catch(() => undefined);

export const begatewayRoutesApi = dashboardApi.injectEndpoints({
    endpoints: (build) => ({
        listGatewayRoutes: build.query<TypeGatewayRoutesList, void>({
            queryFn: async () => {
                let registry: TypeGatewayRegistry | null = null;
                const walked = await walkPages<TypeRoute, TypeRoutePage>({
                    url: '/begateway/routes',
                    fallback: 'Non è stato possibile leggere le rotte dal gateway',
                    pageSize: PAGE_SIZE,
                    maxPages: MAX_PAGES,
                    onPage: (body) => { registry = body.registry ?? registry; },
                });
                if ('error' in walked) return walked;
                const items = walked.data;

                items.sort(byKey);
                return { data: { items, registry } };
            },
            providesTags: ['GatewayRoute'],
        }),

        readGatewayRoute: build.query<TypeRoute, TypeRouteRef>({
            query: (ref) => ({ url: target(ref) }),
            providesTags: (_result, _error, ref) => [{ type: 'GatewayRoute', id: routeKey(ref) }],
        }),

        testGatewayRouteLimits: build.query<TypeLimitsTest, TypeRouteRef & TypeLimitsTestInput>({
            query: ({ kind, name, method, path, limit, endpointLimit }) => ({
                url: `${target({ kind, name })}/limits/test`,
                method: 'post',
                data: { method, path, ...(limit ? { limit } : {}), ...(endpointLimit ? { endpointLimit } : {}) },
            }),
        }),

        createGatewayRoute: build.mutation<TypeRoute, TypeRouteInput>({
            query: (route) => ({ url: '/begateway/routes', method: 'post', data: { route } }),
            onQueryStarted: async (_route, { dispatch, queryFulfilled }) => replaceRoute(dispatch, queryFulfilled),
        }),

        updateGatewayRoute: build.mutation<TypeRoute, TypeRouteRef & { route: TypeRouteInput }>({
            query: ({ kind, name, route }) => ({ url: target({ kind, name }), method: 'patch', data: { route } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeRoute(dispatch, data);
                    dispatch(begatewayRoutesApi.util.invalidateTags([{ type: 'GatewayEndpoint', id: routeKey(data) }]));
                } catch {
                }
            },
        }),

        removeGatewayRoute: build.mutation<TypeRouteRef, TypeRouteRef>({
            query: (ref) => ({ url: target(ref), method: 'delete' }),
            onQueryStarted: async (ref, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    dropRoute(dispatch, ref);
                } catch {
                }
            },
        }),

        freezeGatewayRoute: build.mutation<TypeRoute, TypeRouteRef & TypeRouteFreezeInput>({
            query: ({ kind, name, reason, durationMinutes }) => ({ url: `${target({ kind, name })}/freeze`, method: 'post', data: { reason, ...(durationMinutes ? { durationMinutes } : {}) } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => replaceRoute(dispatch, queryFulfilled),
        }),

        unfreezeGatewayRoute: build.mutation<TypeRoute, TypeRouteRef>({
            query: (ref) => ({ url: `${target(ref)}/unfreeze`, method: 'post' }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => replaceRoute(dispatch, queryFulfilled),
        }),

        updateGatewayRouteLimits: build.mutation<TypeRoute, TypeRouteRef & { limit: TypeRouteLimit }>({
            query: ({ kind, name, limit }) => ({ url: `${target({ kind, name })}/limits`, method: 'patch', data: { limit } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeRoute(dispatch, data);
                    dispatch(begatewayRoutesApi.util.invalidateTags([{ type: 'GatewayEndpoint', id: routeKey(data) }, { type: 'GatewayEndpoint', id: 'SERVICE' }]));
                } catch {
                }
            },
        }),
    }),
});

export const {
    useListGatewayRoutesQuery,
    useReadGatewayRouteQuery,
    useLazyTestGatewayRouteLimitsQuery,
    useCreateGatewayRouteMutation,
    useUpdateGatewayRouteMutation,
    useRemoveGatewayRouteMutation,
    useFreezeGatewayRouteMutation,
    useUnfreezeGatewayRouteMutation,
    useUpdateGatewayRouteLimitsMutation,
} = begatewayRoutesApi;

export default begatewayRoutesApi;
